/**
 * Конфигурация бокового меню админ-панели: разделы и пункты.
 *
 * Вынесено из Sidebar в отдельный модуль, чтобы этим же списком могла
 * пользоваться страница «Мои настройки» (выбор видимых пунктов меню).
 * Иконки — имена из компонента Icon (строки). Гейты по правам — в
 * NAV_ITEM_GATES; пункт без гейта виден всем.
 */

import { getDefaultPage } from '@/lib/navPrefs';

export const NAV_SECTIONS = [
  {
    title: 'Обучение',
    items: [
      { id: 'students',    label: 'Ученики',              icon: 'users',    to: '/students' },
      { id: 'classes',     label: 'Классы',               icon: 'grid',     to: '/classes' },
      { id: 'journal',     label: 'Электронный дневник', icon: 'book',     to: '/journal' },
      { id: 'grading',     label: 'Оценки и комментарии',icon: 'star',     to: '/grading' },
      { id: 'schedule',    label: 'Расписание',           icon: 'calendar', to: '/schedule' },
      { id: 'attendance',  label: 'Посещаемость',         icon: 'check',    to: '/attendance' },
      { id: 'subjects',    label: 'Предметы',             icon: 'book',     to: '/subjects' },
      { id: 'rooms',       label: 'Кабинеты',             icon: 'home',     to: '/rooms' },
      { id: 'lesson_times',label: 'Время уроков',         icon: 'clock',    to: '/lesson-times' },
      { id: 'years_study', label: 'Учебные годы',         icon: 'calendar', to: '/years' },
      { id: 'tariffs',     label: 'Тарифы',               icon: 'tag',      to: '/tariffs' },
    ],
  },
  {
    title: 'Финансы',
    items: [
      { id: 'payments',      label: 'Платежи',     icon: 'wallet', to: '/payments' },
      { id: 'extra_charges', label: 'Начисления',  icon: 'money',  to: '/extras/charges' },
    ],
  },
  {
    title: 'Доп. занятия',
    items: [
      { id: 'extra_schedule', label: 'Расписание',          icon: 'calendar', to: '/extras/schedule' },
      { id: 'extra_lessons',  label: 'Занятия',             icon: 'sparkle',  to: '/extras/lessons' },
      { id: 'extra_subjects', label: 'Предметы',            icon: 'book',     to: '/extras/subjects' },
      { id: 'extra_times',    label: 'Временные интервалы', icon: 'clock',    to: '/extras/times' },
    ],
  },
  {
    title: 'Внеурочные занятия',
    items: [
      { id: 'extracurricular_schedule', label: 'Расписание',          icon: 'calendar', to: '/extracurricular/schedule' },
      { id: 'extracurricular_subjects', label: 'Предметы',            icon: 'book',     to: '/extracurricular/subjects' },
      { id: 'extracurricular_times',    label: 'Временные интервалы', icon: 'clock',    to: '/extracurricular/times' },
      { id: 'extracurricular_students', label: 'Выбор занятий',       icon: 'users',    to: '/extracurricular/selections' },
    ],
  },
  {
    title: 'Коммуникации',
    items: [
      { id: 'messages',          label: 'Сообщения',     icon: 'chat', to: '/messages' },
      { id: 'broadcasts_email',  label: 'Email-рассылки', icon: 'mail', to: '/broadcasts/email' },
    ],
  },
  {
    title: 'Администрирование',
    items: [
      { id: 'calls',              label: 'Записи звонков',      icon: 'phone',    to: '/calls' },
      { id: 'staff',              label: 'Сотрудники',          icon: 'badge',    to: '/staff' },
      { id: 'permission_presets', label: 'Права администраторов', icon: 'gear',   to: '/permission-presets' },
      { id: 'parents',            label: 'Родители',            icon: 'users',    to: '/parents' },
      { id: 'reports',            label: 'Отчёты',              icon: 'chart',    to: '/reports' },
      { id: 'holidays',           label: 'Произв. календарь',   icon: 'calendar', to: '/calendar' },
      { id: 'document_templates', label: 'Шаблоны документов',  icon: 'doc',      to: '/document-templates' },
      { id: 'email_templates',    label: 'Шаблоны писем',       icon: 'mail',     to: '/email-templates' },
      { id: 'settings',           label: 'Настройки',           icon: 'gear',     to: '/settings' },
    ],
  },
  {
    title: 'СКУД',
    items: [
      { id: 'skud_persons', label: 'Люди', icon: 'badge', to: '/skud/persons' },
    ],
  },
];

// Условие, при котором пункт меню вообще показывается. Возвращает bool, получая
// в аргументе функцию `can(key, action)`. Если гейт не задан — пункт виден всем.
export const NAV_ITEM_GATES = {
  settings: (can) =>
    can('settings.mail', 'read')   ||
    can('settings.1c',   'read')   ||
    can('settings.skud', 'read')   ||
    can('settings.ws',   'read')   ||
    can('settings.api_log', 'read') ||
    can('services.manage', 'read'),
  skud_persons: (can) => can('skud.persons', 'read'),
  classes:            (can) => can('classes',            'read'),
  subjects:           (can) => can('subjects',           'read'),
  rooms:              (can) => can('rooms',              'read'),
  schedule:           (can) => can('schedule',           'read'),
  journal:            (can) => can('journal',            'read'),
  grading:            (can) => can('grading_settings',   'read'),
  lesson_times:       (can) => can('timetable_slots',    'read'),
  years_study:        (can) => can('years_study',        'read'),
  tariffs:            (can) => can('tariffs',            'read'),
  payments:           (can) => can('payments',           'read'),
  holidays:           (can) => can('holidays',           'read'),
  document_templates: (can) => can('document_templates', 'read'),
  email_templates:    (can) => can('email_templates',    'read'),
  calls:              (can) => can('telephony.calls',     'read'),
  staff:              (can) => can('staff.administrators', 'read') || can('staff.teachers', 'read'),
  permission_presets: (can) => can('permission_presets',    'read'),
  parents:            (can) => can('staff.parents', 'read'),
  reports:            (can) => can('reports',         'read'),
  extra_subjects:     (can) => can('extra.subjects', 'read'),
  extra_lessons:      (can) => can('extra.lessons',  'read'),
  extra_times:        (can) => can('extra.times',    'read'),
  extra_schedule:     (can) => can('extra.schedule', 'read'),
  extra_charges:      (can) => can('extra.charges',  'read'),
  extracurricular_subjects: (can) => can('extracurricular.subjects', 'read'),
  extracurricular_times:    (can) => can('extracurricular.times',    'read'),
  extracurricular_schedule: (can) => can('extracurricular.schedule', 'read'),
  extracurricular_students: (can) => can('extracurricular.students', 'read'),
  messages:                 (can) => can('messages',                'read'),
  broadcasts_email:         (can) => can('broadcasts.email',         'read'),
};

/**
 * Виден ли пункт пользователю по правам (без учёта персонального скрытия).
 * @param {{id:string}} item
 * @param {(key:string, action:string)=>boolean} can
 */
export function isNavItemVisible(item, can) {
  const gate = NAV_ITEM_GATES[item.id];
  return gate ? gate(can) : true;
}

/**
 * Куда вести при заходе на корень `/` и после входа: выбранная пользователем
 * стартовая страница, если она задана и доступна ему по правам. Иначе —
 * стандартная '/students'. Валидация по правам обязательна: localStorage
 * общий для браузера, поэтому чужой/устаревший выбор не должен закинуть на
 * недоступную страницу.
 *
 * @param {(key:string, action:string)=>boolean} can
 * @returns {string} путь
 */
export function resolveDefaultPath(can) {
  const stored = getDefaultPage();
  if (stored) {
    for (const sec of NAV_SECTIONS) {
      for (const it of sec.items) {
        if (it.to === stored && isNavItemVisible(it, can)) return stored;
      }
    }
  }
  return '/students';
}
